import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Cropper from 'cropperjs';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { MdClose, MdCrop, MdCropFree } from 'react-icons/md';

import { useGetByDevice } from '../hooks/use-responsive';

import 'cropperjs/dist/cropper.css';

export type TCropDialogProps = {
  open: boolean;
  src: string;
  cropAspectRatio?: number;
  onConfirm: (blob: Blob) => void;
  onSkip: () => void;
  onCancel: () => void;
  onExited?: () => void;
};

export const CropDialog: FC<TCropDialogProps> = ({
  open,
  src,
  cropAspectRatio = NaN,
  onConfirm,
  onSkip,
  onCancel,
  onExited,
}) => {
  const { isMobile } = useGetByDevice();
  const [cropperReady, setCropperReady] = useState(false);
  const cropImageRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<Cropper | null>(null);

  useEffect(() => {
    if (!src || !open) {
      return;
    }
    const img = cropImageRef.current;
    if (!img) {
      return;
    }

    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
    setCropperReady(false);
    cropperRef.current = new Cropper(img, {
      viewMode: 1,
      autoCropArea: 0.8,
      aspectRatio: cropAspectRatio,
      movable: true,
      rotatable: true,
      scalable: true,
      zoomable: true,
      responsive: true,
      background: false,
      ready() {
        setCropperReady(true);
      },
    });
    return () => {
      cropperRef.current?.destroy();
      cropperRef.current = null;
    };
  }, [src, open, cropAspectRatio]);

  const handleConfirm = useCallback(() => {
    const cropper = cropperRef.current;
    if (!cropper) {
      return;
    }
    const canvas = cropper.getCroppedCanvas({
      maxWidth: 4096,
      maxHeight: 4096,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });
    canvas.toBlob(
      blob => {
        if (!blob) {
          return;
        }
        cropper.destroy();
        cropperRef.current = null;
        onConfirm(blob);
      },
      'image/webp',
      0.85,
    );
  }, [onConfirm]);

  const handleSkip = useCallback(() => {
    cropperRef.current?.destroy();
    cropperRef.current = null;
    onSkip();
  }, [onSkip]);

  const handleCancel = useCallback(() => {
    cropperRef.current?.destroy();
    cropperRef.current = null;
    onCancel();
  }, [onCancel]);

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') {
          handleCancel();
        }
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      keepMounted
      TransitionProps={{ onExited }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden', bgcolor: '#1a1a1a' }}>
        <Box sx={{ width: '100%', height: isMobile ? '100dvh' : '65vh', position: 'relative' }}>
          {!cropperReady && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress sx={{ color: '#fff' }} />
            </Box>
          )}
          <img ref={cropImageRef} src={src} alt="crop preview" style={{ display: 'block', maxWidth: '100%' }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="outlined" color="error" startIcon={<MdClose />}>
          Cancel
        </Button>
        <Button onClick={handleSkip} variant="outlined" startIcon={<MdCropFree />}>
          {isMobile ? 'Skip' : 'Skip Crop'}
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!cropperReady} startIcon={<MdCrop />}>
          {isMobile ? 'Apply' : 'Apply Crop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
